import Accordion from "@/components/Accordion/Accordion";
import Badge from "@/components/Badge/Badge";
import { Button } from "@/components/Button/Button";
import Dialog from "@/components/Dialog/Dialog";
import { Icon } from "@/components/Icon";
import Separator from "@/components/Separator/Separator";
import Tabs from "@/components/Tabs/Tabs";
import Toggle from "@/components/Toggle/Toggle.component";
import { useContext } from "react";
import { Slot } from "utils/create-slots";
import { DecisionsContext } from "../contexts/decisionContext";
import { UI_LAYER } from "../Usercentrics";

const Service = ({
  name,
  description,
  categorySlug,
  id,
  isEssential,
  consent,
  categories,
  processingCompany,
  dataPurposes,
  technologiesUsed,
  dataCollected,
  legalBasis,
  dataDistribution,
  retentionPeriodDescription,
  dataRecipients,
  urls,
}) => {
  const { decisions, updateDecision } = useContext(DecisionsContext);

  return (
    <Accordion.Item value={id} key={id}>
      <Slot name="header">
        <Accordion.Header className="border rounded border-gray-300 mt-6 p-6 open:border-b-0 open:rounded-b-none">
          <div className="flex gap-7 items-center justify-between w-full">
            <Accordion.Trigger>
              <div className="text-left">
                <span className="text-base font-bold">{name}</span>
                <p className="text-base font-normal">
                  {categories.find(({ slug }) => slug === categorySlug)?.label}
                </p>
              </div>
            </Accordion.Trigger>
            <Toggle
              defaultChecked={consent.status}
              iconChecked={!isEssential ? <Icon type="checkmark" /> : null}
              classChecked={!isEssential ? "!bg-success" : ""}
              disabled={isEssential}
              checked={!!decisions[id]?.status}
              onCheckedChange={(checked: boolean) => {
                updateDecision(id, checked);
              }}
            />
          </div>
        </Accordion.Header>
      </Slot>
      <Slot name="content">
        <Accordion.Content>
          <Separator className=" bg-gray-300" />
          <div className="border-b border-l border-r p-5 border-gray-300 text-sm overflow-hidden">
            <h3 className="text-sm text-gray-600 mb-5">Servicebeschreibung</h3>
            <p>{description}</p>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Verarbeitendes Unternehmen
            </h3>
            <p>
              {processingCompany.name}
              <br />
              {processingCompany.address}
            </p>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Datenschutzbeauftragter der verarbeitenden Firma
            </h3>
            <p>
              Nachfolgend finden Sie die E-Mail-Adresse des
              Datenschutzbeauftragten des verarbeitenden Unternehmens.
              <br />
              <a
                href={processingCompany.dataProtectionOfficer}
                target="_blank"
                rel="noreferrer"
              >
                {processingCompany.dataProtectionOfficer}
              </a>
            </p>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Datenverarbeitungszwecke
            </h3>
            <p>
              Diese Liste stellt die Zwecke der Datenerhebung und -verarbeitung
              dar.
            </p>
            <ul className="flex flex-wrap">
              {dataPurposes.map((purpose, index) => (
                <li key={index}>
                  <Badge className="my-2 mx-2">{purpose}</Badge>
                </li>
              ))}
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Verwendete Technologien
            </h3>
            <p>
              Diese Liste enthält alle Technologien, mit denen dieser Dienst
              Daten sammelt. Typische Technologien sind Cookies und Pixel, die
              im Browser platziert werden.
            </p>
            <ul className="flex flex-wrap">
              {technologiesUsed.map((technology, index) => (
                <li key={index}>
                  <Badge className="my-2 mx-2">{technology}</Badge>
                </li>
              ))}
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">Erhobene Daten</h3>
            <p>
              Diese Liste enthält alle (persönlichen) Daten, die von oder durch
              die Nutzung dieses Dienstes gesammelt werden.
            </p>
            <ul className="flex flex-wrap">
              {dataCollected.map((data: string, index: number) => (
                <li key={index}>
                  <Badge className="my-2 mx-2">{data}</Badge>
                </li>
              ))}
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Rechtliche Grundlage
            </h3>
            <p>
              Im Folgenden wird die erforderliche Rechtsgrundlage für die
              Verarbeitung von Daten genannt.
            </p>
            <ul className="flex flex-wrap">
              {legalBasis.map((legal: string, index: number) => (
                <li key={index}>
                  <Badge className="my-2 mx-2">{legal}</Badge>
                </li>
              ))}
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Ort der Verarbeitung
            </h3>
            <p>
              Dies ist der primäre Ort, an dem die gesammelten Daten verarbeitet
              werden. Sollten die Daten auch in anderen Ländern verarbeitet
              werden, werden Sie gesondert informiert.
            </p>
            <ul>
              <li>
                <Badge className="my-2 mx-2">
                  {dataDistribution.processingLocation}
                </Badge>
              </li>
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Aufbewahrungsdauer
            </h3>
            <p>
              Die Aufbewahrungsfrist ist die Zeitspanne, in der die gesammelten
              Daten für die Verarbeitung gespeichert werden. Die Daten müssen
              gelöscht werden, sobald sie für die angegebenen
              Verarbeitungszwecke nicht mehr benötigt werden.
            </p>
            <ul>
              <li>{retentionPeriodDescription}</li>
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">
              Weitergabe an Drittländer
            </h3>
            <p>
              Dieser Service kann die erfassten Daten an ein anderes Land
              weiterleiten. Bitte beachten Sie, dass dieser Service Daten in ein
              Land, welches kein angemessenes Datenschutzniveau bietet,
              übertragen kann. Falls die Daten in die USA übertragen werden,
              besteht das Risiko, dass Ihre Daten von US Behörden zu Kontroll-
              und Überwachungszwecken verarbeitet werden können, ohne dass Ihnen
              möglicherweise Rechtsbehelfsmöglichkeiten zustehen. Nachfolgend
              finden Sie eine Liste der Länder, in die die Daten übertragen
              werden. Weitere Informationen zu den Sicherheitsgarantien finden
              Sie in den Datenschutzrichtlinien des Website-Anbieters oder
              wenden Sie sich direkt an den Website-Anbieter.
            </p>
            <ul className="flex flex-wrap">
              {dataDistribution.thirdPartyCountries.map((country, index) => (
                <li key={index}>
                  <Badge className="my-2 mx-2">{country}</Badge>
                </li>
              ))}
            </ul>
            <h3 className="text-sm text-gray-600 mb-5 mt-7">Datenempfänger</h3>
            <p>
              Im Folgenden werden die Empfänger der erhobenen Daten aufgelistet.
            </p>
            <ul>
              {dataRecipients.map((recipient, index) => (
                <li key={index}>{recipient}</li>
              ))}
            </ul>
            {urls.privacyPolicy && (
              <>
                <h3 className="text-sm text-gray-600 mb-5 mt-7">
                  Klicken Sie hier, um die Datenschutzbestimmungen des
                  Datenverarbeiters zu lesen
                </h3>
                <p>
                  <a href={urls.privacyPolicy} target="_blank" rel="noreferrer">
                    {urls.privacyPolicy}
                  </a>
                </p>
              </>
            )}
            {urls.cookiePolicy && (
              <>
                <h3 className="text-sm text-gray-600 mb-5 mt-7">
                  Klicken Sie hier, um die Cookie-Richtlinie des
                  Datenverarbeiters zu lesen
                </h3>
                <p>
                  <a href={urls.cookiePolicy} target="_blank" rel="noreferrer">
                    {urls.cookiePolicy}
                  </a>
                </p>
              </>
            )}
          </div>
        </Accordion.Content>
      </Slot>
    </Accordion.Item>
  );
};

const Category = ({ label, slug, description, services, isEssential }) => {
  const { decisions, updateDecision } = useContext(DecisionsContext);

  return (
    <Accordion.Item value={slug} key={slug}>
      <Slot name="header">
        <Accordion.Header className="border rounded border-gray-300 mt-6 p-6 open:border-b-0 open:rounded-b-none">
          <div className="flex gap-7 items-center justify-between w-full">
            <Accordion.Trigger>
              <div className="text-left">
                <span className="text-base font-bold">{label}</span>
                <p className="text-base font-normal text-gray-600">
                  {description}
                </p>
              </div>
            </Accordion.Trigger>
            <Toggle
              iconChecked={!isEssential ? <Icon type="checkmark" /> : null}
              classChecked={!isEssential ? "!bg-success" : ""}
              disabled={isEssential}
              checked={Object.values(decisions)
                .filter(({ categorySlug }) => categorySlug === slug)
                .every(({ status }) => status)}
              onCheckedChange={(checked: boolean) => {
                Object.values(decisions)
                  .filter(({ categorySlug }) => categorySlug === slug)
                  .forEach(({ serviceId }) =>
                    updateDecision(serviceId, checked)
                  );
              }}
            />
          </div>
        </Accordion.Header>
      </Slot>
      <Slot name="content">
        <Accordion.Content>
          <Separator className=" bg-gray-300" />
          <ul className="border-b border-l border-r p-5 border-gray-300">
            {services.map(
              ({ id, name, categorySlug, isEssential, consent }) => (
                <li
                  key={categorySlug}
                  className="flex justify-between mb-6 pr-6"
                >
                  <p>{name}</p>
                  <Toggle
                    defaultChecked={consent.status}
                    iconChecked={
                      !isEssential ? <Icon type="checkmark" /> : null
                    }
                    classChecked={!isEssential ? "!bg-success" : ""}
                    disabled={isEssential}
                    checked={!!decisions[id]?.status}
                    onCheckedChange={(checked: boolean) => {
                      updateDecision(id, checked);
                    }}
                  />
                </li>
              )
            )}
          </ul>
        </Accordion.Content>
      </Slot>
    </Accordion.Item>
  );
};

const SecondLayer = ({
  categories,
  labels,
  onTCFUICloseHandler,
  onAcceptAllHandler,
  onUpdateViewHandler,
  UC,
}) => {
  const { decisions } = useContext(DecisionsContext);

  const onSaveHandler = (decisions) => {
    const userDecisions = Object.values(decisions);
    UC.updateServices(userDecisions).then(() => {
      onUpdateViewHandler(UI_LAYER.PRIVACY_BUTTON);
    });
  };

  return (
    <Dialog
      open={true}
      onOpenChange={onTCFUICloseHandler}
      className="md:!p-9 h-[95vh]"
      width="medium"
    >
      <Slot name="title">
        <Dialog.Title className="text-2xl md:text-3xl">
          {labels?.secondLayer?.title}
        </Dialog.Title>
      </Slot>
      <Slot name="description">
        {labels?.secondLayer?.description && (
          <p
            className="text-small my-4 text-sm"
            dangerouslySetInnerHTML={{
              __html: labels?.secondLayer?.description,
            }}
          />
        )}
      </Slot>
      <Slot name="content">
        <Tabs.Root defaultValue={labels?.secondLayer?.categoryTab}>
          <Tabs.List>
            <div className="flex">
              <Tabs.Trigger
                value={labels?.secondLayer?.categoryTab}
                className="active:border-orange-light border-transparent border-b-4 py-4 mr-8 active:text-blue-light active:font-bold"
              >
                {labels?.secondLayer?.categoryTab}
              </Tabs.Trigger>
              <Tabs.Trigger
                value={labels?.secondLayer?.serviceTab}
                className="active:border-orange-light border-transparent border-b-4 py-4 mr-8 active:text-blue-light active:font-bold"
              >
                {labels?.secondLayer?.serviceTab}
              </Tabs.Trigger>
            </div>
          </Tabs.List>
          <Tabs.Content value={labels?.secondLayer?.categoryTab}>
            <Accordion type="multiple">
              {categories
                .filter(({ isHidden }) => !isHidden)
                .map((category) => (
                  <Category key={category.slug} {...category} />
                ))}
            </Accordion>
          </Tabs.Content>
          <Tabs.Content value={labels?.secondLayer?.serviceTab}>
            <Accordion type="multiple">
              {categories
                .filter(({ isHidden }) => !isHidden)
                .flatMap(({ label, slug, description, services }) => {
                  return (
                    <>
                      {services.map((service) => (
                        <Service
                          key={service.id}
                          {...service}
                          categories={categories}
                        />
                      ))}
                    </>
                  );
                })}
            </Accordion>
          </Tabs.Content>
        </Tabs.Root>

        <div className="flex justify-end mt-6">
          <Button
            className="mr-5"
            color="secondary"
            type="button"
            onClick={() => {
              onSaveHandler(decisions);
            }}
          >
            {labels?.buttons?.save}
          </Button>
          <Button type="button" onClick={onAcceptAllHandler}>
            {labels?.buttons?.acceptAll}
          </Button>
        </div>
      </Slot>
    </Dialog>
  );
};

export default SecondLayer;
